import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Code, Rocket, Users, Mail, CheckCircle, Sparkles, TrendingUp, Zap } from 'lucide-react';
import { METRICS, SERVICES, TESTIMONIALS, TECH_LOGOS, CONTACT } from '../data/mock';
import SEO from '../components/SEO';
import { OrganizationSchema } from '../components/StructuredData';
import ContactForm from '../components/ContactForm';

const HomePage = () => {
  return (
    <div className="min-h-screen overflow-hidden bg-white relative">
      <SEO 
        title="Hire Elite Full-Stack Engineers | React, Node, DevOps & Cloud Excellence"
        description="Architecting enterprise-grade full-stack applications with 15+ years of elite engineering expertise. 30+ successful launches, 150+ sophisticated solutions delivered."
        keywords="hire full-stack engineers, react developers, nodejs developers, devops engineers, cloud developers, frontend backend development, enterprise engineering"
        url="/"
      />
      <OrganizationSchema />

      {/* Geometric Background Elements */}
      <div className="fixed inset-0 pointer-events-none opacity-30 z-0">
        <div className="geometric-pattern" style={{ top: '10%', right: '5%' }} />
        <div className="geometric-pattern" style={{ bottom: '15%', left: '8%', animationDelay: '3s' }} />
        <div className="line-structure" style={{ top: '20%', left: '10%', transform: 'rotate(45deg)' }} />
        <div className="line-structure" style={{ bottom: '30%', right: '15%', transform: 'rotate(-30deg)' }} />
        
        {/* Decorative Bento Boxes */}
        <div className="bento-box-decoration small floating" style={{ top: '15%', right: '8%', animationDelay: '0s' }} />
        <div className="bento-box-decoration medium floating" style={{ top: '45%', left: '5%', animationDelay: '2s' }} />
        <div className="bento-box-decoration large floating" style={{ bottom: '20%', right: '10%', animationDelay: '4s' }} />
        <div className="bento-box-decoration small" style={{ top: '60%', right: '15%', transform: 'rotate(45deg)' }} />
        <div className="bento-box-decoration medium" style={{ bottom: '35%', left: '12%', transform: 'rotate(-20deg)' }} />
        
        {/* Diamond Decorations */}
        <div className="diamond-decoration small animated" style={{ top: '25%', right: '20%', animationDelay: '1s' }} />
        <div className="diamond-decoration large" style={{ bottom: '40%', left: '15%', animationDelay: '3s' }} />
        
        {/* Rectangular Frames (Bento Style) */}
        <div className="rect-frame horizontal" style={{ top: '35%', right: '10%' }} />
        <div className="rect-frame vertical" style={{ bottom: '25%', left: '8%' }} />
        <div className="rect-frame square" style={{ top: '50%', right: '25%', transform: 'rotate(15deg)' }} />
        
        {/* Luxury gradient overlays */}
        <div className="luxury-gradient-overlay" style={{ top: '10%', left: '15%', width: '400px', height: '400px' }} />
        <div className="luxury-gradient-overlay" style={{ bottom: '15%', right: '20%', width: '350px', height: '350px' }} />
        
        {/* Corner brackets for professional touch */}
        <div className="corner-bracket top-left" style={{ top: '25%', left: '10%' }} />
        <div className="corner-bracket bottom-right" style={{ bottom: '30%', right: '12%' }} />
        
        {/* Dot Patterns */}
        <div className="dot-pattern" style={{ top: '15%', left: '5%' }} />
        <div className="dot-pattern" style={{ bottom: '20%', right: '8%' }} />
      </div>

      {/* Animated Background Blobs */}
      <div className="blob blob-1" />
      <div className="blob blob-2" />
      <div className="blob blob-3" />

      {/* Hero Section */}
      <section className="relative pt-28 pb-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-violet-50/50 via-purple-50/30 to-transparent pointer-events-none" />
        <div className="container relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-5xl mx-auto"
          >
            {/* Elite Badge */}
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="mb-10"
            >
              <span className="inline-flex items-center gap-2 px-7 py-4 rounded-full text-white text-base font-bold shadow-xl" style={{
                background: 'linear-gradient(135deg, #4C3362 0%, #3C284D 100%)',
                boxShadow: '0 20px 40px rgba(76, 51, 98, 0.4)'
              }}>
                <Sparkles className="w-5 h-5" />
                {METRICS.customSolutions} High-Impact Solutions Delivered
              </span>
            </motion.div>

            {/* Main Headline */}
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="display-lg mb-8 leading-tight"
            >
              <span className="gradient-text">Great Engineers</span>
              <br />
              Building For You
            </motion.h1>

            {/* Subtitle */}
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="text-xl text-slate-700 mb-10 max-w-3xl mx-auto leading-relaxed font-medium"
            >
              Work with great engineers bringing 15+ years of full-stack experience to your systems, from refined React interfaces and resilient Node services to disciplined DevOps automation and cloud architectures built to last. With 30+ launches and 150+ bespoke solutions built, we shape how your software is engineered, with quality kept front and center.
            </motion.p>

            {/* CTA Buttons */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="flex flex-wrap gap-4 justify-center"
            >
              <Link to="/contact" className="btn-primary">
                Get Started
                <ArrowRight className="w-5 h-5" />
              </Link>
              <Link to="/services" className="btn-secondary">
                <Code className="w-5 h-5" />
                Explore Capabilities
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Metrics Bento Grid */}
      <section className="py-10 relative">
        <div className="container">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bento-card text-center bg-gradient-to-br from-purple-50 to-violet-50"
            >
              <TrendingUp className="w-12 h-12 mx-auto mb-4 text-[#4C3362]" />
              <div className="text-5xl font-bold gradient-text mb-2">{METRICS.yearsExperience}</div>
              <div className="text-slate-700 font-semibold text-lg mb-1">Years of Mastery</div>
              <div className="text-sm text-slate-600">A track record built on consistency and technical depth</div>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bento-card text-center bg-gradient-to-br from-teal-50 to-cyan-50"
            >
              <Rocket className="w-12 h-12 mx-auto mb-4" style={{ color: '#28D9C3' }} />
              <div className="text-5xl font-bold mb-2" style={{ color: '#28D9C3' }}>{METRICS.startupsLaunched}</div>
              <div className="text-slate-700 font-semibold text-lg mb-1">Enterprise Launches</div>
              <div className="text-sm text-slate-600">Complex products guided from concept to stable release</div>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="bento-card text-center bg-gradient-to-br from-slate-50 to-gray-50"
            >
              <Zap className="w-12 h-12 mx-auto mb-4" style={{ color: '#212227' }} />
              <div className="text-5xl font-bold mb-2" style={{ color: '#212227' }}>{METRICS.customSolutions}</div>
              <div className="text-slate-700 font-semibold text-lg mb-1">Bespoke Solutions</div>
              <div className="text-sm text-slate-600">Tailored systems engineered with meticulous attention to detail</div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Mosaic Grid */}
      <section className="py-10 bg-gradient-to-b from-white to-slate-50">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <h2 className="display-sm mb-4 gradient-text">Elite Engineering Capabilities</h2>
            <p className="text-lg text-slate-700 max-w-2xl mx-auto font-medium">
              Full-stack architecture shaped with care, discipline, and a commitment to long-term reliability.
            </p>
          </motion.div>

          <div className="bento-grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 max-w-7xl mx-auto">
            {SERVICES.map((service, idx) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
              >
                <Link 
                  to={`/services/${service.slug}`}
                  className="bento-card group cursor-pointer h-full flex flex-col"
                >
                  <div 
                    className="w-14 h-14 rounded-xl mb-4 flex items-center justify-center shadow-lg transition-transform group-hover:scale-110"
                    style={{ 
                      background: `linear-gradient(135deg, ${service.color}20, ${service.color}10)`,
                      border: `2px solid ${service.color}30`
                    }}
                  >
                    <Code className="w-7 h-7" style={{ color: service.color }} />
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-slate-900 group-hover:text-[#4C3362] transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed mb-4 flex-grow">{service.shortDesc}</p>
                  <div className="flex items-center gap-2 text-sm font-semibold" style={{ color: service.color }}>
                    Explore Capability
                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-2" />
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Technology Excellence */}
      <section className="py-10 relative">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <h2 className="display-sm mb-4 gradient-text">Technology Mastery</h2>
            <p className="text-lg text-slate-700 max-w-2xl mx-auto font-medium">
              Elite tooling and frameworks for enterprise-grade architecture
            </p>
          </motion.div>

          <div className="bento-grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 max-w-4xl mx-auto">
            {['react', 'nodejs', 'python', 'aws', 'docker', 'kubernetes', 'typescript', 'postgresql', 'mongodb', 'terraform', 'nextjs', 'graphql'].map((tech) => (
              <motion.div
                key={tech}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                className="bento-card p-6 text-center group cursor-pointer"
              >
                <img src={TECH_LOGOS[tech]} alt={tech} className="w-10 h-10 mx-auto mb-2 transition-transform group-hover:scale-110" />
                <span className="text-xs font-semibold text-slate-600 capitalize">
                  {tech.replace('dotjs', '.js').replace('nextjs', 'Next.js').replace('nodejs', 'Node.js')}
                </span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-12 bg-gradient-to-b from-slate-50 to-white">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <h2 className="display-sm mb-4 gradient-text">Client Excellence</h2>
            <p className="text-lg text-slate-700 font-medium">Testimonials from enterprise leaders</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {TESTIMONIALS.map((testimonial, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="bento-card"
              >
                <div className="flex items-center gap-4 mb-4">
                  <div 
                    className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold shadow-lg"
                    style={{ background: `linear-gradient(135deg, ${testimonial.color}, ${testimonial.color}dd)` }}
                  >
                    {testimonial.author.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div>
                    <div className="font-bold text-slate-900">{testimonial.author}</div>
                    <div className="text-xs text-slate-600">{testimonial.role}</div>
                  </div>
                </div>
                <p className="text-slate-700 text-sm leading-relaxed italic">"{testimonial.quote}"</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-16 bg-gradient-to-b from-white to-slate-50 relative">
        <div className="container max-w-5xl">
          <ContactForm ctaText="Ready to Transform Your Vision Into Reality?" />
        </div>
      </section>
    </div>
  );
};

export default HomePage;
